import React, { useState } from "react";
import { 
  Container, Navbar, Nav, NavDropdown, 
  Badge, Card, Row, Col, ProgressBar 
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { 
  Gear, Bell, Search, BoxArrowRight, 
  PersonCircle, Cart, ClockHistory, Receipt, 
  Send, Paperclip 
} from "react-bootstrap-icons";
import "./UserDashboardLayout.css";

// Import your page components
import SendRequestPage from "./SendRequestPage";
import AttachReceiptPage from "./AttachReceiptPage";
import GetPerformaInvoicePage from "./GetPerformaInvoicePage";
import AccountSettingsPage from "./AccountSettingsPage";

function UserDashboardLayout() {
  const [activeMenu, setActiveMenu] = useState("dashboard-overview");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [notifications] = useState(3);
  const [user] = useState({  // Mock user data
    name: "Henok A.",
    email: "henok@example.com",
    avatar: <PersonCircle size={32} className="text-light" />
  });

  // Mock data for dashboard overview
  const userStats = {
    totalOrders: 15,
    pendingRequests: 2,
    completedOrders: 10,
    orderTrend: 65
  };

  const recentOrders = [
    { id: 1, product: "Wireless Headphones", status: "Delivered", date: "2024-03-15" },
    { id: 2, product: "Smart Watch", status: "Processing", date: "2024-03-18" },
    { id: 3, product: "Laptop Backpack", status: "Shipped", date: "2024-03-19" },
  ];

  const renderContent = () => {
    switch (activeMenu) {
      case "dashboard-overview":
        return (
          <div className="content-section">
            <div className="dashboard-header mb-4">
              <h4>Welcome back, {user.name}!</h4>
              <p className="text-muted">Here's your activity summary</p>
            </div>
            <Row className="g-4 mb-4">
              <Col md={3}>
                <Card className="stat-card">
                  <Card.Body>
                    <div className="d-flex align-items-center">
                      <div className="stat-icon bg-primary">
                        <Cart size={24} />
                      </div>
                      <div className="ms-3">
                        <h6 className="mb-0">Total Requests</h6>
                        <h3>{userStats.totalOrders}</h3>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="stat-card">
                  <Card.Body>
                    <div className="d-flex align-items-center">
                      <div className="stat-icon bg-warning">
                        <ClockHistory size={24} />
                      </div>
                      <div className="ms-3">
                        <h6 className="mb-0">Pending Requests</h6>
                        <h3>{userStats.pendingRequests}</h3>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="stat-card">
                  <Card.Body>
                    <div className="d-flex align-items-center">
                      <div className="stat-icon bg-success">
                        <Receipt size={24} />
                      </div>
                      <div className="ms-3">
                        <h6 className="mb-0">Completed Orders</h6>
                        <h3>{userStats.completedOrders}</h3>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Card className="mb-4">
              <Card.Body>
                <h5 className="mb-3">Order Trend</h5>
                <ProgressBar now={userStats.orderTrend} variant="success" />
              </Card.Body>
            </Card>

            <Card>
              <Card.Body>
                <h5 className="mb-3">Recent Orders</h5>
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>Product</th>
                        <th>Status</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentOrders.map(order => (
                        <tr key={order.id}>
                          <td>#{order.id}</td>
                          <td>{order.product}</td>
                          <td>
                            <Badge bg="info">{order.status}</Badge>
                          </td>
                          <td>{order.date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
          </div>
        );
      case "send-request":
        return <SendRequestPage />;
      case "attach-receipt":
        return <AttachReceiptPage />;
      case "get-performa-invoice":
        return <GetPerformaInvoicePage />;
      case "settings":
        return <AccountSettingsPage user={user} />;
      default:
        return <div>Select a menu item</div>;
    }
  };

  return (
    <div className="dashboard-wrapper">
      {/* Sidebar */}
      <div className={`sidebar bg-dark text-white ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <h5 className="text-center mb-0">E-Commerce Dashboard</h5>
        </div>
        <Nav className="flex-column mt-4">
          {[
            { id: "dashboard-overview", icon: <ClockHistory />, text: "Overview" },
            { id: "send-request", icon: <Send />, text: "Send Request" },
            { id: "attach-receipt", icon: <Paperclip />, text: "Attach Receipt" },
            { id: "get-performa-invoice", icon: <Receipt />, text: "Proforma Invoice" },
            { id: "settings", icon: <Gear />, text: "Settings" },
          ].map((item) => (
            <Nav.Link
              key={item.id}
              className={`sidebar-item ${activeMenu === item.id ? 'active' : ''}`}
              onClick={() => setActiveMenu(item.id)}
            >
              <span className="sidebar-icon">{item.icon}</span>
              {!sidebarCollapsed && item.text}
            </Nav.Link>
          ))}
        </Nav>
      </div>

      {/* Main Content */}
      <div className={`main-content ${sidebarCollapsed ? 'expanded' : ''}`}>
        {/* Top Navigation Bar */}
        <Navbar expand="lg" className="top-navbar shadow-sm">
          <Container fluid>
            <button 
              className="sidebar-toggle btn btn-link" 
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            >
              ☰
            </button>
            
            <Navbar.Collapse id="navbar-nav">
              <Nav className="ms-auto align-items-center">
                <form className="search-form">
                  <div className="input-group">
                    <input
                      type="search"
                      className="form-control"
                      placeholder="Search orders..."
                    />
                    <button className="btn btn-primary">
                      <Search />
                    </button>
                  </div>
                </form>

               
                <NavDropdown
                  title={
                    <div className="d-flex align-items-center">
                      {user.avatar}
                      <div className="ms-2 d-none d-sm-block">
                        <div className="fw-normal small">{user.name}</div>
                        <div className="text-muted extra-small">{user.email}</div>
                      </div>
                    </div>
                  }
                  align="end"
                  className="user-dropdown"
                >
                  <NavDropdown.Item onClick={() => setActiveMenu("settings")}>
                    <Gear className="me-2" /> Account Settings
                  </NavDropdown.Item>
                  <NavDropdown.Divider />
                  <NavDropdown.Item as={Link} to="/logout">
                    <BoxArrowRight className="me-2" /> Logout
                  </NavDropdown.Item>
                </NavDropdown>
              </Nav>
            </Navbar.Collapse>
          </Container>
        </Navbar>

        {/* Dynamic Content Area */}
        <div className="content-container p-4">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

export default UserDashboardLayout;